import { AnyFinancialEntity } from '../../financialEntities';
import { Goal, GoalTransaction } from '../../goals';
import { TransactionScheduleFieldsFragment } from '../../graphql/generated';
import { sortBy } from '../../util/arrays';
import { dt } from '../../util/dateTime';
import pluralize from '../../util/pluralize';
import { useFinancialEntitiesContext } from '../financialEntities';
import { isFailedTransactionStatus } from '../transactions';
import { useCurrentUser } from '../user';
import { getGoalTransactions, getGoalTransactionSchedules } from './helpers';

export type GoalTransferEntity = (
    | (GoalTransaction & {
          transactionSchedule?: TransactionScheduleFieldsFragment;
      })
    | TransactionScheduleFieldsFragment
) & {
    nextTransferDate: string | undefined;
    source?: AnyFinancialEntity;
    destination?: AnyFinancialEntity;
};

export const getNextGoalTransferDate = (
    te: GoalTransaction | TransactionScheduleFieldsFragment
): string | undefined => {
    if (te.__typename === 'TransactionSchedule') {
        if (te.status !== 'ACTIVE') return;
        return te.nextTransactionDate;
    } else {
        return te.plannedProcessingDate;
    }
};

export const useGoalTransferEntities = (goal?: Goal): GoalTransferEntity[] => {
    const { featureFlags } = useCurrentUser();
    const { fes } = useFinancialEntitiesContext();
    if (!goal || !fes.unfilteredData) return [];

    const transactions = getGoalTransactions(goal);
    // Exclude all transaction schedules once Shutdown Phase 1 is enabled
    const transactionSchedules = featureFlags.isShutdownV1Enabled
        ? []
        : getGoalTransactionSchedules(goal);

    const makeTransferEntity = (
        te: GoalTransaction | TransactionScheduleFieldsFragment
    ): GoalTransferEntity => {
        const entity = {
            ...te,
            nextTransferDate: getNextGoalTransferDate(te),
            source: fes.unfilteredData?.financialEntities.find(
                (fe) => fe.id === te.sourceId
            ),
            destination: fes.unfilteredData?.financialEntities.find(
                (fe) => fe.id === te.destinationId
            ),
        };

        if (
            entity.__typename === 'Transaction' &&
            !!entity.transactionScheduleId
        ) {
            const transactionSchedule = transactionSchedules.find(
                (ts) => ts.id === entity.transactionScheduleId
            );
            return {
                ...entity,
                transactionSchedule,
            };
        }

        return entity;
    };

    /* Active transfers are:
     *   1. Next transfer for active transaction schedules
     *   2. All non-terminal transactions where processing date is >= today
     *   3. All failed transactions where processing date is within last 14 days
     */
    const activeTransfers = sortBy(
        [...transactionSchedules, ...transactions].map(makeTransferEntity),
        (te) => te.nextTransferDate ?? '',
        'ASC'
    ).filter(
        (te) =>
            !dt(te.nextTransferDate).isBefore(dt().toDateStr()) ||
            (isFailedGoalTransfer(te) &&
                !dt(te.nextTransferDate).isBefore(
                    dt().subtract(14, 'days').toDateStr()
                ))
    );

    return activeTransfers;
};

export const getGoalTransferTitleStr = (
    transferEntities: GoalTransferEntity[]
) => {
    const failedTransfers: GoalTransferEntity[] = [];
    const scheduledTransfers: GoalTransferEntity[] = [];

    for (const transfer of transferEntities) {
        if (isFailedGoalTransfer(transfer)) {
            failedTransfers.push(transfer);
        } else {
            scheduledTransfers.push(transfer);
        }
    }

    let title = `${pluralize('transfer', scheduledTransfers.length, true)} scheduled`;
    if (failedTransfers.length > 0) {
        title += `, ${failedTransfers.length} failed`;
    }

    return title;
};

export const isFailedGoalTransfer = (transfer: GoalTransferEntity) => {
    return (
        transfer.__typename === 'Transaction' &&
        isFailedTransactionStatus(transfer.status)
    );
};
